import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, User, Users, Download, Eye, RotateCcw, MapPin, X, Share2 } from "lucide-react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import PasswordInput from "@/components/common/PasswordInput";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const AforoPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [userName, setUserName] = useState("");
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [credentials, setCredentials] = useState("");

  // Check for credentials from centralized login
  useEffect(() => {
    const storedCreds = localStorage.getItem('taytantos_credentials');
    const storedRole = localStorage.getItem('taytantos_user_role');
    const storedNombre = localStorage.getItem('taytantos_user_nombre');
    
    if (storedCreds && storedRole === 'aforo') {
      setCredentials(storedCreds);
      setUserName(storedNombre || '');
      setIsLoggedIn(true);
      // Clear the stored credentials after use
      localStorage.removeItem('taytantos_credentials');
      localStorage.removeItem('taytantos_user_role');
      localStorage.removeItem('taytantos_user_nombre');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    try {
      const response = await axios.post(`${API}/aforo/login`, loginForm);
      if (response.data.success) {
        setIsLoggedIn(true);
        setUserName(response.data.user);
        setCredentials(btoa(`${loginForm.username}:${loginForm.password}`));
      } else {
        setLoginError(response.data.message);
      }
    } catch (e) {
      setLoginError("Error de conexión");
    }
  };

  const fetchStats = useCallback(async () => {
    if (!isLoggedIn || !credentials) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/aforo/stats`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setStats(response.data);
    } catch (e) {
      console.error("Error fetching stats:", e);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, credentials]);

  const fetchEventStats = async (eventId) => {
    try {
      const response = await axios.get(`${API}/aforo/stats/${eventId}`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setEventStats(response.data);
      setSelectedEvent(eventId);
    } catch (e) {
      console.error("Error fetching event stats:", e);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchStats();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, fetchStats]);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginForm({ username: "", password: "" });
    setCredentials("");
    setStats([]);
    setSelectedEvent(null);
    setEventStats(null);
  };

  if (!isLoggedIn) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="form-section w-full max-w-md mx-4 relative z-10">
          <div className="text-center mb-8">
            <h1 className="font-logo text-3xl mb-2">
              <span className="text-white neon-glow">TAYTANTOS</span>
              <span className="text-white neon-glow text-2xl" style={{fontFamily: "'Monoton', cursive"}}>pass</span>
            </h1>
            <p className="text-[var(--accent-cyan)] uppercase tracking-widest text-sm">Control de Aforo</p>
          </div>
          
          {loginError && <div className="error-message mb-4">{loginError}</div>}
          
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Usuario</label>
              <input
                type="text"
                className="custom-input"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                required
                data-testid="aforo-login-username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <input
                type="password"
                className="custom-input"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                required
                data-testid="aforo-login-password"
              />
            </div>
            <button type="submit" className="btn-download w-full mt-4" data-testid="aforo-login-btn">
              <Eye size={18} className="inline mr-2" />
              Acceder al Panel de Aforo
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-logo text-2xl">
              <span className="text-white neon-glow">TAYTANTOS</span>
              <span className="text-white neon-glow text-xl" style={{fontFamily: "'Monoton', cursive"}}>pass</span>
            </h1>
            <p className="text-[var(--accent-cyan)] text-sm uppercase">Control de Aforo</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[var(--text-muted)] text-sm">
              <User size={14} className="inline mr-1" />
              {userName}
            </span>
            <button onClick={handleLogout} className="btn-secondary text-sm px-4 py-2">
              <LogOut size={14} className="inline mr-1" />
              Salir
            </button>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <button 
            onClick={fetchStats} 
            className="btn-download text-sm px-4 py-2"
            disabled={loading}
          >
            <RotateCcw size={14} className={`inline mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* Stats Grid */}
        {loading && stats.length === 0 ? (
          <div className="text-center py-12">
            <div className="spinner-large mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.map(event => (
              <div 
                key={event.event_id} 
                className="form-section cursor-pointer hover:border-[var(--accent-cyan)] transition-colors"
                onClick={() => fetchEventStats(event.event_id)}
                data-testid={`aforo-event-${event.event_id}`}
              >
                <h3 className="text-white font-bold text-lg mb-2">{event.event_name}</h3>
                <p className="text-[var(--text-muted)] text-sm mb-4">
                  {event.fecha} • {event.hora} • {event.sala}
                </p>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[var(--text-muted)]">Ocupación</span>
                    <span className="text-[var(--accent-cyan)]">{event.porcentaje_ocupacion}%</span>
                  </div>
                  <div className="h-3 bg-[var(--bg)] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[var(--accent-cyan)] transition-all"
                      style={{ width: `${event.porcentaje_ocupacion}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-center mb-4">
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-[var(--primary)]">{event.total_reservas}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase">Adquiridas</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-[var(--success)]">{event.total_validadas}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase">Validadas</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-[var(--accent-cyan)]">{event.total_disponibles}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase">Disponibles</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-orange-400">{event.media_edad || '-'}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase">Edad Media</p>
                  </div>
                </div>
                
                {/* Gender Stats */}
                {event.genero_stats && (
                  <div className="border-t border-[var(--border-color)] pt-3 mt-3">
                    <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Distribución por género</p>
                    <div className="flex items-center justify-center gap-6">
                      <div className="w-24 h-24">
                        <Doughnut
                          data={{
                            labels: ['Hombres', 'Mujeres'],
                            datasets: [{
                              data: [event.genero_stats.hombres || 0, event.genero_stats.mujeres || 0],
                              backgroundColor: ['#60a5fa', '#f472b6'],
                              borderWidth: 0
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: { display: false }
                            },
                            cutout: '60%'
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                          <span className="text-sm text-white">{event.genero_stats.hombres || 0} Hombres</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-pink-400 rounded-full"></div>
                          <span className="text-sm text-white">{event.genero_stats.mujeres || 0} Mujeres</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Postal Codes Stats - Bar Chart */}
                {event.codigos_postales && event.codigos_postales.length > 0 && (
                  <div className="border-t border-[var(--border-color)] pt-3 mt-3">
                    <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Procedencia de Asistentes</p>
                    <div className="h-40">
                      <Bar
                        data={{
                          labels: event.codigos_postales.slice(0, 8).map(cp => cp.localidad),
                          datasets: [{
                            data: event.codigos_postales.slice(0, 8).map(cp => cp.cantidad),
                            backgroundColor: '#00d4ff',
                            borderRadius: 4
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y',
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const cp = event.codigos_postales[context.dataIndex];
                                  return `${cp.cantidad} reservas (${cp.provincia})`;
                                }
                              }
                            }
                          },
                          scales: {
                            x: {
                              ticks: { color: '#888', font: { size: 10 } },
                              grid: { color: '#333' }
                            },
                            y: {
                              ticks: { color: '#fff', font: { size: 11 } },
                              grid: { display: false }
                            }
                          }
                        }}
                      />
                    </div>
                    {/* Province summary */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {[...new Set(event.codigos_postales.map(cp => cp.provincia))].map((prov, idx) => (
                        <span key={idx} className="text-xs bg-[var(--surface-highlight)] px-2 py-0.5 rounded text-[var(--text-muted)]">
                          {prov}: {event.codigos_postales.filter(cp => cp.provincia === prov).reduce((sum, cp) => sum + cp.cantidad, 0)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Event Detail Modal */}
        {selectedEvent && eventStats && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--surface)] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
                <div>
                  <h3 className="font-heading text-xl text-white uppercase">{eventStats.event_name}</h3>
                  <p className="text-[var(--text-muted)] text-sm">{eventStats.fecha} • {eventStats.hora}</p>
                </div>
                <button 
                  onClick={() => { setSelectedEvent(null); setEventStats(null); }}
                  className="p-2 text-[var(--text-muted)] hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6">
                {/* Main Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="text-center p-4 bg-[var(--bg)]">
                    <p className="text-3xl font-bold text-[var(--primary)]">{eventStats.total_reservas}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase mt-1">Entradas Adquiridas</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg)]">
                    <p className="text-3xl font-bold text-[var(--success)]">{eventStats.total_validadas}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase mt-1">Entradas Validadas</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg)]">
                    <p className="text-3xl font-bold text-orange-400">{eventStats.pendientes_validar}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase mt-1">Pendientes</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg)]">
                    <p className="text-3xl font-bold text-[var(--accent-cyan)]">{eventStats.total_disponibles}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase mt-1">Disponibles</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg)] border-l-4 border-purple-500">
                    <p className="text-3xl font-bold text-purple-400">{eventStats.media_edad || '-'}</p>
                    <p className="text-xs text-[var(--text-muted)] uppercase mt-1">Edad Media</p>
                  </div>
                </div>
                
                {/* Progress Bars */}
                <div className="mb-6">
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--text-muted)]">Ocupación Total</span>
                      <span className="text-[var(--primary)]">{eventStats.porcentaje_ocupacion}%</span>
                    </div>
                    <div className="h-4 bg-[var(--bg)] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[var(--primary)] transition-all"
                        style={{ width: `${eventStats.porcentaje_ocupacion}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--text-muted)]">Entradas Validadas</span>
                      <span className="text-[var(--success)]">{eventStats.porcentaje_validadas}%</span>
                    </div>
                    <div className="h-4 bg-[var(--bg)] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[var(--success)] transition-all"
                        style={{ width: `${eventStats.porcentaje_validadas}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {/* Analytics - Visitas y Compartidos */}
                <div className="grid grid-cols-2 gap-4 mb-6 mt-6">
                  <div className="bg-[var(--bg)] p-4 text-center border-l-4 border-blue-500">
                    <p className="text-3xl font-bold text-blue-400">{eventStats.visitas || 0}</p>
                    <p className="text-[var(--text-muted)] text-xs uppercase mt-1">Visitas al Evento</p>
                    <p className="text-[var(--text-muted)] text-xs">(accesos al enlace)</p>
                  </div>
                  <div className="bg-[var(--bg)] p-4 text-center border-l-4 border-purple-500">
                    <p className="text-3xl font-bold text-purple-400">{eventStats.compartidos || 0}</p>
                    <p className="text-[var(--text-muted)] text-xs uppercase mt-1">Veces Compartido</p>
                    <p className="text-[var(--text-muted)] text-xs">(redes sociales)</p>
                  </div>
                </div>
                
                {/* Evolución de Ventas por Día */}
                {eventStats.ventas_por_dia && eventStats.ventas_por_dia.length > 0 && (
                  <div className="mb-6" data-testid="ventas-por-dia-chart">
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wide">Evolución de Ventas por Día</h4>
                    <div className="bg-[var(--bg)] p-4" style={{ height: '220px' }}>
                      <Bar
                        data={{
                          labels: eventStats.ventas_por_dia.map(d => {
                            const [y, m, day] = d.fecha.split('-');
                            return `${day}/${m}`;
                          }),
                          datasets: [
                            {
                              label: 'Venta Directa',
                              data: eventStats.ventas_por_dia.map(d => d.directas ?? d.cantidad),
                              backgroundColor: 'rgba(0, 212, 255, 0.7)',
                              borderColor: '#00d4ff',
                              borderWidth: 1
                            },
                            {
                              label: 'Vía RRPP',
                              data: eventStats.ventas_por_dia.map(d => d.rrpp ?? 0),
                              backgroundColor: 'rgba(236, 0, 232, 0.7)',
                              borderColor: '#ec00e8',
                              borderWidth: 1
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: true, labels: { color: '#a1a1aa', font: { size: 11 } } },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} entradas`,
                                footer: (items) => `Total: ${items.reduce((s, i) => s + i.parsed.y, 0)} entradas`
                              }
                            }
                          },
                          scales: {
                            x: { stacked: true, ticks: { color: '#a1a1aa', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } },
                            y: { stacked: true, beginAtZero: true, ticks: { color: '#a1a1aa', precision: 0 }, grid: { color: 'rgba(255,255,255,0.08)' } }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Tramos Info */}
                {eventStats.tramos_enabled && eventStats.tramos && (
                  <div>
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wide">Detalle por Tramos</h4>
                    <div className="space-y-3">
                      {eventStats.tramos.map((tramo, idx) => (
                        <div key={idx} className={`p-3 bg-[var(--bg)] border-l-4 ${tramo.activo ? 'border-[var(--accent-cyan)]' : 'border-gray-600'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white font-bold">{tramo.nombre}</span>
                            {tramo.activo && <span className="text-xs bg-[var(--accent-cyan)] text-black px-2 py-1 font-bold">ACTIVO</span>}
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center text-sm">
                            <div>
                              <p className="text-[var(--primary)] font-bold">{tramo.reservadas}</p>
                              <p className="text-[var(--text-muted)] text-xs">Adquiridas</p>
                            </div>
                            <div>
                              <p className="text-[var(--success)] font-bold">{tramo.validadas}</p>
                              <p className="text-[var(--text-muted)] text-xs">Validadas</p>
                            </div>
                            <div>
                              <p className="text-[var(--accent-cyan)] font-bold">{tramo.disponibles}</p>
                              <p className="text-[var(--text-muted)] text-xs">Disponibles</p>
                            </div>
                            <div>
                              <p className="text-white font-bold">{tramo.cantidad}</p>
                              <p className="text-[var(--text-muted)] text-xs">Total</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* RRPP Statistics */}
                <div className="mt-6">
                  <h4 className="text-white font-bold mb-4 uppercase tracking-wide flex items-center gap-2">
                    <Share2 size={18} className="text-[var(--primary)]" />
                    Ventas por Canal
                  </h4>
                  
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-[var(--bg)] p-4 text-center">
                      <p className="text-2xl font-bold text-[var(--accent-cyan)]">{eventStats.ventas_directas || 0}</p>
                      <p className="text-[var(--text-muted)] text-xs uppercase">Venta Directa</p>
                    </div>
                    <div className="bg-[var(--bg)] p-4 text-center">
                      <p className="text-2xl font-bold text-[var(--primary)]">{eventStats.total_rrpp_ventas || 0}</p>
                      <p className="text-[var(--text-muted)] text-xs uppercase">Via RRPP</p>
                    </div>
                  </div>
                  
                  {/* RRPP List */}
                  {eventStats.rrpp_stats && eventStats.rrpp_stats.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-[var(--text-muted)] text-xs mb-2">Detalle por RRPP:</p>
                      {eventStats.rrpp_stats.map((rrpp, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-[var(--bg)] p-3 border-l-4 border-[var(--primary)]">
                          <div>
                            <p className="text-white font-bold">{rrpp.nombre}</p>
                            <p className="text-[var(--text-muted)] text-xs">
                              Código: {rrpp.code}
                              {rrpp.telefono && ` • ${rrpp.telefono}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-[var(--primary)]">{rrpp.total_ventas}</p>
                            <p className="text-[var(--text-muted)] text-xs">
                              {rrpp.validadas} validadas
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[var(--text-muted)] text-sm text-center py-4 bg-[var(--bg)]">
                      No hay RRPP asignados a este evento
                    </p>
                  )}
                </div>

                {/* Mesas Statistics */}
                {eventStats.mesas_enabled && eventStats.mesas_stats && (
                  <div className="mt-6">
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wide flex items-center gap-2">
                      <Users size={18} className="text-yellow-400" />
                      Mesas
                    </h4>
                    
                    {/* Mesas Summary */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-[var(--bg)] p-3 text-center border-l-4 border-yellow-400">
                        <p className="text-xl font-bold text-yellow-400">{eventStats.mesas_stats.reservadas}</p>
                        <p className="text-[var(--text-muted)] text-xs">Reservadas</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-[var(--accent-cyan)]">{eventStats.mesas_stats.disponibles}</p>
                        <p className="text-[var(--text-muted)] text-xs">Disponibles</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-white">{eventStats.mesas_stats.total_personas}</p>
                        <p className="text-[var(--text-muted)] text-xs">Personas</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-green-400">{eventStats.mesas_stats.ingresos_online?.toFixed(0) || 0}€</p>
                        <p className="text-[var(--text-muted)] text-xs">Cobrado</p>
                      </div>
                    </div>
                    
                    {/* Mesas Payment Status */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-900/30 p-3 text-center border border-green-600/30">
                        <p className="text-lg font-bold text-green-400">{eventStats.mesas_stats.pagadas_online || 0}</p>
                        <p className="text-green-400/70 text-xs">Pagadas Online</p>
                      </div>
                      <div className="bg-yellow-900/30 p-3 text-center border border-yellow-600/30">
                        <p className="text-lg font-bold text-yellow-400">{eventStats.mesas_stats.pendientes_pago_online || 0}</p>
                        <p className="text-yellow-400/70 text-xs">Pendientes Online</p>
                      </div>
                      <div className="bg-blue-900/30 p-3 text-center border border-blue-600/30">
                        <p className="text-lg font-bold text-blue-400">{eventStats.mesas_stats.pago_en_local || 0}</p>
                        <p className="text-blue-400/70 text-xs">Pago en Local</p>
                      </div>
                    </div>
                    
                    {/* Mesas List */}
                    {eventStats.mesas_stats.reservaciones?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[var(--text-muted)] text-xs mb-2">Detalle de reservas:</p>
                        {eventStats.mesas_stats.reservaciones.map((mesa, idx) => (
                          <div key={idx} className={`flex items-center justify-between bg-[var(--bg)] p-3 border-l-4 ${mesa.pagado ? 'border-green-500' : 'border-yellow-500'}`}>
                            <div>
                              <p className="text-white font-bold">Mesa #{mesa.numero_mesa} - {mesa.nombre}</p>
                              <p className="text-[var(--text-muted)] text-xs">
                                {mesa.telefono} • {mesa.num_personas} personas
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 ${
                                mesa.pagado 
                                  ? 'bg-green-900/50 text-green-400' 
                                  : 'bg-yellow-900/50 text-yellow-400'
                              }`}>
                                {mesa.metodo_pago === 'online' 
                                  ? (mesa.pagado ? '✓ Pagado Online' : '⏳ Pendiente')
                                  : 'Pago en Local'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reservados Statistics */}
                {eventStats.reservados_enabled && eventStats.reservados_stats && (
                  <div className="mt-6">
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wide flex items-center gap-2">
                      <Users size={18} className="text-[var(--accent-cyan)]" />
                      Reservados VIP
                    </h4>
                    
                    {/* Reservados Summary */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-[var(--bg)] p-3 text-center border-l-4 border-[var(--accent-cyan)]">
                        <p className="text-xl font-bold text-[var(--accent-cyan)]">{eventStats.reservados_stats.reservados}</p>
                        <p className="text-[var(--text-muted)] text-xs">Reservados</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-[var(--primary)]">{eventStats.reservados_stats.disponibles}</p>
                        <p className="text-[var(--text-muted)] text-xs">Disponibles</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-white">{eventStats.reservados_stats.total_personas}</p>
                        <p className="text-[var(--text-muted)] text-xs">Personas</p>
                      </div>
                      <div className="bg-[var(--bg)] p-3 text-center">
                        <p className="text-xl font-bold text-green-400">{eventStats.reservados_stats.ingresos_online?.toFixed(0) || 0}€</p>
                        <p className="text-[var(--text-muted)] text-xs">Cobrado</p>
                      </div>
                    </div>
                    
                    {/* Reservados Payment Status */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-900/30 p-3 text-center border border-green-600/30">
                        <p className="text-lg font-bold text-green-400">{eventStats.reservados_stats.pagados_online || 0}</p>
                        <p className="text-green-400/70 text-xs">Pagados Online</p>
                      </div>
                      <div className="bg-yellow-900/30 p-3 text-center border border-yellow-600/30">
                        <p className="text-lg font-bold text-yellow-400">{eventStats.reservados_stats.pendientes_pago_online || 0}</p>
                        <p className="text-yellow-400/70 text-xs">Pendientes Online</p>
                      </div>
                      <div className="bg-blue-900/30 p-3 text-center border border-blue-600/30">
                        <p className="text-lg font-bold text-blue-400">{eventStats.reservados_stats.pago_en_local || 0}</p>
                        <p className="text-blue-400/70 text-xs">Pago en Local</p>
                      </div>
                    </div>
                    
                    {/* Reservados List */}
                    {eventStats.reservados_stats.reservaciones?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[var(--text-muted)] text-xs mb-2">Detalle de reservas:</p>
                        {eventStats.reservados_stats.reservaciones.map((reservado, idx) => (
                          <div key={idx} className={`flex items-center justify-between bg-[var(--bg)] p-3 border-l-4 ${reservado.pagado ? 'border-green-500' : 'border-yellow-500'}`}>
                            <div>
                              <p className="text-white font-bold">Reservado #{reservado.numero_mesa} - {reservado.nombre}</p>
                              <p className="text-[var(--text-muted)] text-xs">
                                {reservado.telefono} • {reservado.num_personas} personas
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 ${
                                reservado.pagado 
                                  ? 'bg-green-900/50 text-green-400' 
                                  : 'bg-yellow-900/50 text-yellow-400'
                              }`}>
                                {reservado.metodo_pago === 'online' 
                                  ? (reservado.pagado ? '✓ Pagado Online' : '⏳ Pendiente')
                                  : 'Pago en Local'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Gender and Location Stats Charts */}
                {(eventStats.genero_stats || eventStats.codigos_postales) && (
                  <div className="mt-6">
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wide">
                      Demografía de Asistentes
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Gender Chart */}
                      {eventStats.genero_stats && (eventStats.genero_stats.hombres > 0 || eventStats.genero_stats.mujeres > 0) && (
                        <div className="bg-[var(--bg)] p-4 rounded">
                          <p className="text-[var(--text-muted)] text-xs uppercase mb-3">Distribución por Género</p>
                          <div className="flex items-center justify-center gap-6">
                            <div className="w-28 h-28">
                              <Doughnut
                                data={{
                                  labels: ['Hombres', 'Mujeres'],
                                  datasets: [{
                                    data: [eventStats.genero_stats.hombres || 0, eventStats.genero_stats.mujeres || 0],
                                    backgroundColor: ['#60a5fa', '#f472b6'],
                                    borderWidth: 0
                                  }]
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: true,
                                  plugins: { legend: { display: false } },
                                  cutout: '60%'
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
                                <span className="text-white text-lg font-bold">{eventStats.genero_stats.hombres || 0}</span>
                                <span className="text-[var(--text-muted)] text-sm">Hombres</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-pink-400 rounded-full"></div>
                                <span className="text-white text-lg font-bold">{eventStats.genero_stats.mujeres || 0}</span>
                                <span className="text-[var(--text-muted)] text-sm">Mujeres</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Location Chart */}
                      {eventStats.codigos_postales && eventStats.codigos_postales.length > 0 && (
                        <div className="bg-[var(--bg)] p-4 rounded">
                          <p className="text-[var(--text-muted)] text-xs uppercase mb-3">Procedencia</p>
                          <div className="h-44">
                            <Bar
                              data={{
                                labels: eventStats.codigos_postales.slice(0, 6).map(cp => cp.localidad),
                                datasets: [{
                                  data: eventStats.codigos_postales.slice(0, 6).map(cp => cp.cantidad),
                                  backgroundColor: '#00d4ff',
                                  borderRadius: 4
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                indexAxis: 'y',
                                plugins: {
                                  legend: { display: false },
                                  tooltip: {
                                    callbacks: {
                                      label: (ctx) => {
                                        const cp = eventStats.codigos_postales[ctx.dataIndex];
                                        return `${cp.cantidad} reservas (${cp.provincia})`;
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  x: { ticks: { color: '#888' }, grid: { color: '#333' } },
                                  y: { ticks: { color: '#fff', font: { size: 11 } }, grid: { display: false } }
                                }
                              }}
                            />
                          </div>
                          {/* Province totals */}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {[...new Set(eventStats.codigos_postales.map(cp => cp.provincia))].map((prov, idx) => (
                              <span key={idx} className="text-xs bg-[var(--surface-highlight)] px-2 py-0.5 rounded text-[var(--accent-cyan)]">
                                {prov}: {eventStats.codigos_postales.filter(cp => cp.provincia === prov).reduce((sum, cp) => sum + cp.cantidad, 0)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="mt-6 pt-4 border-t border-[var(--border-color)]">
                  <p className="text-[var(--text-muted)] text-sm">
                    <MapPin size={14} className="inline mr-1" />
                    {eventStats.sala} - {eventStats.direccion}
                  </p>
                  <p className="text-[var(--text-muted)] text-xs mt-2">
                    Capacidad máxima: {eventStats.max_tickets} entradas
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// QR Scanner Component

export default AforoPanel;
